import {
  getInsuranceTypeDisplayName,
  IProductHolding,
  ISelfServicePageAnalytics,
  OwnedAssistanceProduct,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import { useAccountStore } from '@/store';
import { addDays, isBefore } from 'date-fns';
import { RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';
import { clone } from 'lodash-es';
import { NO, YES } from '@/constants/general-words.const';
import { FormEvents } from '@/enums/form-events.enum';
import { IAnalyticsForm } from '@/interfaces/forms/analytics-interface';
import { IInsurancePolicyStatusRules } from './self-service-rules/InsurancePolicyStatusRules';

export enum PageEvents {
  PAGE_VIEW = 'pageView',
  PAGE_SEARCH = 'pageSearch',
}

export enum SiteSection {
  MY_RACQ = 'MyRACQ',
  RACQ = 'RACQ',
}

export class PageAnalyticsBuilder {
  accountStore = useAccountStore();
  pageAnalytics: ISelfServicePageAnalytics = {
    event: `${SiteSection.MY_RACQ}.${PageEvents.PAGE_VIEW}`,
  };

  constructor(overrideTitle?: string) {
    // Populate basic identity details
    this.pageAnalytics.profileInfo = {
      bankHoldings: !!this.accountStore.identityDetails?.portfolio?.bankingProducts?.length,
      birthYear: this.accountStore.identityDetails?.birthDate?.getFullYear(),
      contactId: this.accountStore.identityDetails?.d365Id,
      memberLevel: this.accountStore.identityDetails?.membershipLoyalty,
      numberOfProducts:
        (this.accountStore.identityDetails?.portfolio?.insurancePolicies?.length ?? 0) +
        (this.accountStore.identityDetails?.portfolio?.assistanceProducts?.length ?? 0),
      postcode: this.accountStore.identityDetails?.addresses?.find((address) => address.addressType == 'Postal')
        ?.postcode,
      productHoldingArray: [],
    };
    // Populate page basic info
    this.pageAnalytics.webPageDetails = {
      name: overrideTitle || document.title,
      siteSection: SiteSection.MY_RACQ, // default to MyRACQ
    };
  }
  populateRsaProductDetail(assistance: OwnedAssistanceProduct): IProductHolding {
    const policy: IProductHolding = {
      autoRenew: assistance.isAutoPayment ? 'On' : 'Off',
      discountApplied: 'No discounts',
      duePayment: assistance.status == 'Activated' ? 'Payment not due' : 'Payment due',
      excessUse: assistance.productCode == RSA_ROADSIDE_EXCESS_USE ? YES : NO,
      hasDirectDebit: assistance.isAutoPayment ? YES : NO,
      paymentFrequency: 'Annual',
      policyStatus: assistance.status,
      productCategory: 'Assistance',
      productName: assistance.tierDescription,
      renewalStatus: assistance.status == 'Renewal Produced' ? 'In renewal' : 'Not in renewal',
      termEndDate: assistance.endDate,
    };
    const vehicle = assistance.vehicleDetail;
    if (vehicle) {
      policy.vehicleDetails = [
        `${vehicle.manufacturingYear} ${vehicle.make?.description} ${vehicle.model?.description}`,
      ];
    }
    return policy;
  }
  /**
   * Populate portfolio data
   *
   * @param dictionary dictionary needs to be initialed within vue setup
   */
  populateProducts(
    dictionary: Record<string, string>,
    policyStatusRules?: IInsurancePolicyStatusRules,
    policyNumber?: string,
    paymentMethod?: string
  ) {
    const insurancePolicies =
      (policyNumber?.length ?? 0) > 0
        ? this.accountStore.identityDetails?.portfolio?.insurancePolicies?.filter(
            (x) => x.policyNumber.toLowerCase() === policyNumber?.toLowerCase()
          )
        : this.accountStore.identityDetails?.portfolio?.insurancePolicies;

    insurancePolicies?.forEach((insurance) => {
      const policy: IProductHolding = {
        autoRenew: insurance.currentRenewalPreference ? 'On' : 'Off',
        discountApplied: insurance.coverages[0]?.discounts.length ? insurance.coverages[0]?.discounts : 'No discounts',
        duePayment: (insurance.renewalTermPaymentDetails ?? insurance.currentTermPaymentDetails).isPaymentDue
          ? 'Payment due'
          : 'Payment not due',
        hasDirectDebit: insurance.currentTermPaymentDetails.discriminator == 'NonResponsivePaymentDetails' ? YES : NO,
        paymentFrequency: insurance.currentTermPaymentDetails.isAnnual === false ? 'Monthly' : 'Annual',
        // cancelled policy will be filtered out by osapi, we can only detect future cancellation
        policyStatus: insurance.cancellationDate || insurance.state.isFutureCancelled ? 'Cancelled' : insurance.status,
        productCategory: 'Insurance',
        productName: getInsuranceTypeDisplayName(dictionary, insurance.coverages?.[0]?.coverageCode),
        renewalStatus: insurance.renewalTermPaymentDetails ? 'In renewal' : 'Not in renewal',
        termEndDate: insurance.termEnd,
        paymentMethod: paymentMethod ?? 'No payment method',
      };

      const homeDetails = insurance.coverages
        ?.filter((coverage) => coverage as ResidentialCoverage)
        ?.map((coverage) => {
          const residentialCoverage = coverage as ResidentialCoverage;

          return {
            buildingSumInsured: residentialCoverage.buildingSumInsured ?? undefined,
            contentSumInsured: residentialCoverage?.generalContentsAmount ?? undefined,
            occupancyType: residentialCoverage?.ownerOccupancyCode ?? undefined,
          };
        });

      if (homeDetails.find((x) => x.buildingSumInsured !== undefined)) {
        policy.buildingSumInsured = homeDetails.map((x) => x.buildingSumInsured)[0];
      }

      if (homeDetails.find((x) => x.contentSumInsured !== undefined)) {
        policy.contentSumInsured = homeDetails.map((x) => x.contentSumInsured)[0];
      }

      if (homeDetails.find((x) => x.occupancyType !== undefined)) {
        policy.occupancyType = homeDetails.map((x) => x.occupancyType)[0];
      }

      if (policyStatusRules) {
        policy.changesScheduled = policyStatusRules.showFutureChange ? 'ChangesScheduled' : 'None';
      }

      // Set vehicle
      policy.combinedPolicy = insurance.coverages?.length > 1 ? YES : NO;
      const vehicleDetails = insurance.coverages
        ?.filter((coverage) => (coverage as VehicleCoverage).vehicle)
        ?.map((coverage) => {
          const vehicle = (coverage as VehicleCoverage).vehicle;
          return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
        });
      vehicleDetails && (policy.vehicleDetails = vehicleDetails);

      const valueCategory = insurance.coverages
        ?.filter((coverage) => coverage as VehicleCoverage)
        ?.map((coverage) => {
          return (coverage as VehicleCoverage).isMarketValue;
        })[0]
        ? 'IsMarketValue'
        : 'AgreedValue';
      policy.valueCategory = valueCategory;

      let optionalBenefits;
      //vehicle optional benefits
      optionalBenefits = insurance.coverages
        ?.filter((coverage) => (coverage as VehicleCoverage).benefits?.length ?? 0 > 0)
        ?.map((b: any) => {
          return b.benefits;
        })
        .join(',');

      if (!optionalBenefits || optionalBenefits.length === 0) {
        optionalBenefits = insurance.coverages
          ?.filter((coverage) => coverage as ResidentialCoverage)
          ?.map((c) => {
            const residentialCoverage = c as ResidentialCoverage;
            const optionalBenefits = [];
            if (residentialCoverage.hasBuildingAdvancedCover) optionalBenefits.push('Building advanced cover');
            if (residentialCoverage.hasContentsAdvancedCover) optionalBenefits.push('Content advanced cover');
            if (residentialCoverage.hasBuildingMotorBurnout) optionalBenefits.push('Building motor burnout');
            if (residentialCoverage.hasContentsMotorBurnout) optionalBenefits.push('Content motor burnout');
            if (residentialCoverage.petsCovered ?? 0 > 0) optionalBenefits.push('Pet cover');

            return optionalBenefits;
          })
          .join(',');
      }

      policy.optionalBenefits =
        optionalBenefits && optionalBenefits.length > 0 && optionalBenefits !== ','
          ? optionalBenefits
          : 'No optional benefits';

      // Set grace period
      if ((insurance.status as any) == 'Cancelled' && insurance.cancellationDate) {
        const gracePeriodEnd = addDays(insurance.cancellationDate, 30);
        policy.gracePeriod = isBefore(new Date(), gracePeriodEnd) ? YES : NO; // If today is before grace period end, insurance is within grace period
      }

      this.pageAnalytics.profileInfo!.productHoldingArray!.push(policy);
    });

    if (!policyNumber || policyNumber.length === 0) {
      this.accountStore.identityDetails?.portfolio?.assistanceProducts?.forEach((assistance) => {
        const policy = this.populateRsaProductDetail(assistance);
        this.pageAnalytics.profileInfo!.productHoldingArray!.push(policy);
      });
    }
  }

  populateRsaProductData(roadside: OwnedAssistanceProduct) {
    const policy = this.populateRsaProductDetail(roadside);
    this.pageAnalytics.profileInfo!.productHoldingArray!.push(policy);
  }

  populateModalStart(
    event: FormEvents,
    isAuthenticated: boolean,
    stepName: string,
    name: string,
    termEndDate?: Date,
    contactId?: string
  ) {
    this.pageAnalytics.event = event;
    this.pageAnalytics.form = {} as IAnalyticsForm;
    this.pageAnalytics.form.authStatus = isAuthenticated ? 'Logged in' : 'Not logged in';
    this.pageAnalytics.form.stepName = stepName;
    this.pageAnalytics.form.name = name;
    this.pageAnalytics.form.termEndDate = termEndDate?.toString();

    this.pageAnalytics.profileInfo = {
      contactId: contactId,
    };
  }

  /**
   * Push data to adobe layout
   */
  pushAnalytics() {
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(clone(this.pageAnalytics)); // use clone to remove vue proxy
  }
}
